import { useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import { ColumnDef } from "@tanstack/react-table"
import { InboxIcon } from "@heroicons/react/24/outline"
import { Layout } from "../components/Layout"
import { Button } from "@/components/Button"
import { CommsTabs } from "@/components/CommsTabs"
import { DataTable } from "@/components/DataTable/DataTable"
import { TableSkeleton } from "@/components/Skeleton/Skeleton"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { useResponseEvents, type ResponseEvent } from "@/hooks/useResponseEvents"
import { draftFromEvent } from "@/lib/composeDraft"

/**
 * Comms → Events. Things people did that a response is ready for, newest first. Pick one
 * and Compose opens with the message written and the right link already in it.
 *
 * Events describe product activity, such as a diagrams power user. Responses describe
 * the suggested outreach, such as an invitation to Discord.
 */

const pill = "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"

function ago(hours: number): string {
  if (hours < 48) return `${hours} h ago`
  return `${Math.floor(hours / 24)} d ago`
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
}

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" })
}

function StatusCell({ e }: { e: ResponseEvent }) {
  if (e.suppressed)
    return (
      <span className={`${pill} bg-rose-100 text-rose-800`}>
        {e.suppressed === "complained" ? "Reported us as spam" : "Address bounced"}
      </span>
    )
  if (e.handled_at) {
    const chip = (
      <span className={`${pill} bg-blue-100 text-blue-800`}>
        {e.response_id === "join-discord" ? "Invitation recorded" : "Email sent"} {formatDay(e.handled_at)}
      </span>
    )
    // The contact page lists their threads: the note we sent, and any reply.
    return e.contact_public_id ? (
      <Link
        to={`/comms/contacts/${e.contact_public_id}`}
        title="Open their threads"
        className="hover:opacity-80"
      >
        {chip}
      </Link>
    ) : (
      chip
    )
  }
  if (e.response_blocked_reason)
    return <span className="text-xs text-muted-foreground">{e.response_blocked_reason}</span>
  if (e.handoff === "expired")
    return <span className={`${pill} bg-amber-100 text-amber-800`}>Hand-off expired</span>
  if (e.handoff === "unavailable")
    return <span className={`${pill} bg-amber-100 text-amber-800`}>Hand-off unusable</span>
  return <span className="text-xs text-muted-foreground">Not contacted</span>
}

export function CommsEvents() {
  const navigate = useNavigate()
  const { data, isLoading, isFetching, error, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } = useResponseEvents()

  const columns = useMemo<ColumnDef<ResponseEvent>[]>(
    () => [
      {
        id: "when",
        header: "When",
        accessorFn: (e) => e.hours_ago,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            {ago(row.original.hours_ago)}
          </span>
        ),
      },
      {
        id: "who",
        header: "Who",
        accessorFn: (e) => `${e.name ?? ""} ${e.email}`,
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-primary">
              {row.original.name ?? row.original.email}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {row.original.name ? `${row.original.email} · ` : ""}
              {row.original.response_id === "join-discord"
                ? "Diagram creator"
                : row.original.team_name ?? "No team"}
            </p>
          </div>
        ),
      },
      {
        id: "what",
        header: "Event",
        cell: ({ row }) => (
          <div>
            <p className="text-sm text-primary">{row.original.what}</p>
            <p className="text-xs text-muted-foreground">
              {row.original.response_id === "join-discord"
                ? "5+ diagrams created · Active within 72 hours"
                : row.original.handoff === "live"
                  ? "Started from a diagram"
                  : row.original.handoff === "expired"
                    ? "Started from a diagram, hand-off expired"
                    : row.original.handoff === "unavailable"
                      ? "Started from a diagram, hand-off unusable"
                      : "No diagram hand-off"}
            </p>
          </div>
        ),
      },
      {
        id: "response",
        header: "Response",
        cell: ({ row }) => (
          <span className={`${pill} bg-blue-100 text-blue-800`}>{row.original.response_name}</span>
        ),
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => <StatusCell e={row.original} />,
      },
      {
        id: "action",
        header: "",
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="outline"
            disabled={row.original.suppressed !== null || !!row.original.response_blocked_reason}
            title={
              row.original.response_blocked_reason ?? (row.original.suppressed
                ? "Compose refuses to send to this address."
                : row.original.handled_at
                  ? "Already sent this response. Opens Compose with a warning."
                  : "Open Compose with the message written")
            }
            onClick={() => navigate("/email/compose", { state: draftFromEvent(row.original) })}
          >
            {row.original.handled_at ? "Write again" : "Write email"}
          </Button>
        ),
      },
    ],
    [navigate]
  )

  if (error && !data) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  const events = data?.events ?? []

  return (
    <Layout>
      <div className="space-y-6">
        <CommsTabs />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-primary">Comms · Events</h1>
            <p className="text-muted-foreground">
              Things people did that we have a response ready for. Write an email and the right
              link is already in it.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {data && (
              <span className="text-sm text-muted-foreground">
                Loaded {formatTime(data.generated_at)}. It doesn&apos;t update on its own.
              </span>
            )}
            <Button size="sm" variant="outline" disabled={isFetching} onClick={() => refetch()}>
              {isFetching && !isLoading ? "Refreshing…" : "Refresh"}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <TableSkeleton rows={6} />
        ) : events.length === 0 ? (
          <EmptyState
            icon={<InboxIcon className="h-12 w-12" />}
            title={hasNextPage ? "More activity is available" : "Nobody is waiting on a response"}
            description="Upload follow-ups and diagrams power users (5+ diagrams created and active within 72 hours) appear here."
          />
        ) : (
          <DataTable columns={columns} data={events} searchKey="who" />
        )}
        {error && data && <p role="alert">Unable to load more events. Try again or refresh to restart.</p>}
        {hasNextPage && (
          <Button variant="outline" disabled={isFetching} onClick={() => fetchNextPage()}>
            {isFetchingNextPage ? "Loading more…" : "Load more events"}
          </Button>
        )}
      </div>
    </Layout>
  )
}
